const DROPDOWN_SELECTOR = '.picky-option__dropdown';
const OPTION_SELECTOR = '.picky-option';
const HIDDEN_OPTION_SELECTOR = '.picky-option[class*="_option_hidden_"]';
const CARD_SELECTOR = '.picky-product-card';
const GIFT_LABEL_SELECTOR = '[class*="_giftLabel_"]';
const OPEN_CLASS = 'amplify-buy-x-get-y-widget__dropdown--open';
const ENHANCED_ATTR = 'data-amplify-custom-select';
const LIST_CLASS = 'amplify-buy-x-get-y-widget__dropdown-list';
const ITEM_CLASS = 'amplify-buy-x-get-y-widget__dropdown-item';
const ITEM_SELECTED_CLASS =
  'amplify-buy-x-get-y-widget__dropdown-item--selected';
const TITLE_UNAVAILABLE_CLASS =
  'amplify-buy-x-get-y-widget__dropdown-title--unavailable';
const SWATCH_CLASS = 'amplify-buy-x-get-y-widget__swatch';
const COLOR_CHANGE_EVENT = 'gift_block_color_change';
const CARD_SOLD_OUT_CLASS = 'amplify-buy-x-get-y-widget__card--sold-out';
const WIDGET_EMPTY_CLASS = 'amplify-buy-x-get-y-widget--empty';

const optionLabel = option => option?.textContent?.trim() ?? '';

let dropdownIdCounter = 0;

class AmplifyBuyXGetYWidget extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._colorMap = this._readColorMap();
    this._prevLabelBySelect = new WeakMap();

    this._onDocumentPointerDown = this._handleDocumentPointerDown.bind(this);
    this._onWidgetMutations = this._handleWidgetMutations.bind(this);

    document.addEventListener('pointerdown', this._onDocumentPointerDown);

    this._observer = new MutationObserver(this._onWidgetMutations);
    this._observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    this._runSync();
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocumentPointerDown);
    this._observer?.disconnect();
    this._initialized = false;
  }

  _readColorMap() {
    try {
      const parsed = JSON.parse(this.dataset.colorMap || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  // Amplify's own gift badge copy comes from the app settings; the design asks for
  // theme-controlled wording, so it is overwritten here. Only writes on an actual
  // change, otherwise the widget MutationObserver would loop on its own edit.
  _applyGiftLabel() {
    const text = this.dataset.giftLabel?.trim();
    if (!text) return;

    this.querySelectorAll(GIFT_LABEL_SELECTOR).forEach(label => {
      if (label.textContent.trim() !== text) label.textContent = text;
    });
  }

  _runSync() {
    if (this._syncing) return;
    this._syncing = true;

    this._observer?.disconnect();
    try {
      this._applyGiftLabel();
      this._bindDropdowns();
      this._updateAvailability();
    } finally {
      this._observer?.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
      this._syncing = false;
    }
  }

  _handleWidgetMutations() {
    if (this._syncFrame) return;
    this._syncFrame = requestAnimationFrame(() => {
      this._syncFrame = null;
      this._runSync();
    });
  }

  _handleDocumentPointerDown(event) {
    const openDropdowns = this.querySelectorAll(
      `${DROPDOWN_SELECTOR}.${OPEN_CLASS}`
    );
    if (!openDropdowns.length) return;

    const target =
      event.target instanceof Element
        ? event.target
        : event.target?.parentElement;
    if (!target) return;

    const clickedInsideOpen = Array.from(openDropdowns).some(dropdown =>
      dropdown.contains(target)
    );
    if (clickedInsideOpen) return;

    this._closeAllDropdowns();
  }

  _closeAllDropdowns() {
    this.querySelectorAll(`${DROPDOWN_SELECTOR}.${OPEN_CLASS}`).forEach(
      dropdown => this._closeDropdown(dropdown)
    );
  }

  _closeDropdown(dropdown) {
    dropdown.classList.remove(OPEN_CLASS);
    dropdown
      .querySelector('[class*="_head_"]')
      ?.setAttribute('aria-expanded', 'false');
  }

  _focusListEdge(list, edge) {
    const items = Array.from(
      list.querySelectorAll(`.${ITEM_CLASS}:not(:disabled)`)
    );
    if (!items.length) return;
    (edge === 'first' ? items[0] : items[items.length - 1]).focus();
  }

  // Colour swatches only exist on the Color option; Size and any other option render
  // as plain text. Amplify tags each option with the raw (per-market) option name.
  _isColorOption(dropdown) {
    const name = dropdown.closest(OPTION_SELECTOR)?.dataset.optionName?.trim();
    if (!name) return false;

    const known = window.COLOR_OPTION_NAMES || ['Color'];
    return known.includes(name);
  }

  // Amplify's <option> elements carry no swatch data, so the hex is resolved from the
  // theme's `color` metaobject map passed in via data-color-map.
  _swatchColor(dropdown, option) {
    if (!this._isColorOption(dropdown)) return '';
    return this._colorMap[optionLabel(option).toLowerCase()] || '';
  }

  _renderSwatch(container, color, before) {
    let swatch = container.querySelector(`.${SWATCH_CLASS}`);

    if (!color) {
      swatch?.remove();
      return;
    }

    if (!swatch) {
      swatch = document.createElement('span');
      swatch.className = SWATCH_CLASS;
      container.insertBefore(swatch, before ?? container.firstChild);
    }
    if (swatch.style.backgroundColor !== color) {
      swatch.style.backgroundColor = color;
    }
  }

  _syncHeadTitle(dropdown, select) {
    const title = dropdown.querySelector(
      '[class*="_head_"] [class*="_title_"]'
    );
    if (!title) return;

    const selectedOption = select.options[select.selectedIndex];
    title.textContent = optionLabel(selectedOption);
    title.classList.toggle(
      TITLE_UNAVAILABLE_CLASS,
      Boolean(selectedOption?.disabled)
    );

    const head = title.parentElement;
    if (head) {
      this._renderSwatch(
        head,
        this._swatchColor(dropdown, selectedOption),
        title
      );
    }
  }

  _buildList(dropdown, select, list) {
    list.replaceChildren();

    Array.from(select.options).forEach(option => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = ITEM_CLASS;
      item.disabled = option.disabled;

      const swatchColor = this._swatchColor(dropdown, option);
      if (swatchColor) {
        const swatch = document.createElement('span');
        swatch.className = SWATCH_CLASS;
        swatch.style.backgroundColor = swatchColor;
        item.appendChild(swatch);
      }

      const label = document.createElement('span');
      label.textContent = optionLabel(option);
      item.appendChild(label);

      item.setAttribute('role', 'option');
      if (option.selected) {
        item.classList.add(ITEM_SELECTED_CLASS);
        item.setAttribute('aria-selected', 'true');
      }

      item.addEventListener('click', () => {
        if (option.disabled) return;

        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        select.dispatchEvent(new Event('input', { bubbles: true }));

        this._closeDropdown(dropdown);
      });

      list.appendChild(item);
    });
  }

  _bindDropdown(dropdown) {
    if (dropdown.hasAttribute(ENHANCED_ATTR)) return;

    const select = dropdown.querySelector('select');
    let head = dropdown.querySelector('[class*="_head_"]');
    if (!select || !head) return;

    // Amplify renders the trigger as a plain <div>; the accessibility standard bans
    // role='button' on non-semantic elements, so promote it to a real <button> instead
    // of adding role/tabindex. Its CSS/query selectors all key off the class attribute
    // (`[class*="_head_"]`), which a tag swap doesn't affect.
    if (head.tagName !== 'BUTTON') {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = head.className;
      while (head.firstChild) button.appendChild(head.firstChild);
      head.replaceWith(button);
      head = button;
    }

    dropdown.setAttribute(ENHANCED_ATTR, 'true');
    select.classList.add('amplify-buy-x-get-y-widget__native-select');
    select.tabIndex = -1;

    const list = document.createElement('div');
    list.id = `amplify-bxgy-dropdown-${dropdownIdCounter++}`;
    list.className = LIST_CLASS;
    list.setAttribute('role', 'listbox');
    dropdown.appendChild(list);

    head.setAttribute('aria-expanded', 'false');
    head.setAttribute('aria-controls', list.id);

    const stopPointer = event => event.stopPropagation();
    dropdown.addEventListener('mousedown', stopPointer, true);
    dropdown.addEventListener('pointerdown', stopPointer, true);

    const open = () => {
      if (dropdown.classList.contains(OPEN_CLASS)) return;

      const enabledOptions = Array.from(select.options).filter(
        opt => !opt.disabled
      );
      if (select.disabled && enabledOptions.length <= 1) return;

      this._closeAllDropdowns();
      this._buildList(dropdown, select, list);
      dropdown.classList.add(OPEN_CLASS);
      head.setAttribute('aria-expanded', 'true');
    };

    const toggle = () => {
      if (dropdown.classList.contains(OPEN_CLASS)) {
        this._closeDropdown(dropdown);
        return;
      }
      open();
    };

    head.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      toggle();
    });

    head.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
      if (event.key === 'Escape') this._closeDropdown(dropdown);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open();
        this._focusListEdge(list, event.key === 'ArrowDown' ? 'first' : 'last');
      }
    });

    list.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this._closeDropdown(dropdown);
        head.focus();
        return;
      }

      const items = Array.from(
        list.querySelectorAll(`.${ITEM_CLASS}:not(:disabled)`)
      );
      if (!items.length) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const currentIndex = items.indexOf(document.activeElement);
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex =
          currentIndex === -1
            ? delta === 1
              ? 0
              : items.length - 1
            : (currentIndex + delta + items.length) % items.length;
        items[nextIndex].focus();
      }
      if (event.key === 'Home') {
        event.preventDefault();
        items[0].focus();
      }
      if (event.key === 'End') {
        event.preventDefault();
        items[items.length - 1].focus();
      }
    });

    this._trackSelectPrevious(select);

    select.addEventListener('change', () => {
      this._pushColorChange(dropdown, select);
      this._syncHeadTitle(dropdown, select);
      this._buildList(dropdown, select, list);
    });

    new MutationObserver(() => {
      // Amplify can reset the selection on its own; keep the "from" baseline in sync
      // so the next user change does not report a stale previous colour.
      this._trackSelectPrevious(select);
      this._syncHeadTitle(dropdown, select);
      this._buildList(dropdown, select, list);
      this._updateAvailability();
    }).observe(select, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['selected', 'disabled']
    });

    this._syncHeadTitle(dropdown, select);
    this._buildList(dropdown, select, list);
  }

  _bindDropdowns() {
    this.querySelectorAll(
      `${DROPDOWN_SELECTOR}:not([${ENHANCED_ATTR}])`
    ).forEach(dropdown => {
      // Amplify emits a hidden single-value option alongside the real selector.
      if (dropdown.closest(HIDDEN_OPTION_SELECTOR)) return;
      this._bindDropdown(dropdown);
    });
  }

  // BMS-5574 req 6c: a gift whose colours are all sold out (every <option> disabled)
  // must not show a dropdown nobody can pick from — hide its card. If that leaves no
  // card visible, hide the whole widget (title included) rather than an empty shell.
  // The same "empty shell" also shows up when this product simply isn't part of any
  // Buy X Get Y campaign at all — Amplify's app block server-renders zero cards for
  // it up front (confirmed live: card count is already final at DOMContentLoaded,
  // before Amplify's own bundle/pricing fetches have even started — there's no
  // client-side race to wait out here). `_runSync` re-invokes this on every real
  // mutation the widget's own MutationObserver sees, so if that ever changes and a
  // card shows up later, this corrects itself on the next mutation — no timer needed.
  _updateAvailability() {
    const cards = Array.from(this.querySelectorAll(CARD_SELECTOR));
    let anyCardVisible = false;

    cards.forEach(card => {
      const colorSelects = Array.from(
        card.querySelectorAll(`${DROPDOWN_SELECTOR} select`)
      ).filter(select =>
        this._isColorOption(select.closest(DROPDOWN_SELECTOR))
      );

      const allColorsSoldOut =
        colorSelects.length > 0 &&
        colorSelects.every(select =>
          Array.from(select.options).every(option => option.disabled)
        );

      card.classList.toggle(CARD_SOLD_OUT_CLASS, allColorsSoldOut);
      if (!allColorsSoldOut) anyCardVisible = true;
    });

    this.classList.toggle(WIDGET_EMPTY_CLASS, !anyCardVisible);
  }

  _trackSelectPrevious(select) {
    this._prevLabelBySelect.set(
      select,
      optionLabel(select.options[select.selectedIndex])
    );
  }

  _giftProductName(dropdown) {
    const card = dropdown.closest(CARD_SELECTOR);
    const title = card?.querySelector('.picky-product-card__title');
    if (!title) return '';

    // The gift badge lives inside the title element, so take the inner name span.
    return (
      title.querySelector('[class*="_inner_"]')?.textContent?.trim() ||
      title.getAttribute('aria-label')?.trim() ||
      ''
    );
  }

  _pushColorChange(dropdown, select) {
    if (!this._isColorOption(dropdown)) return;

    const from = this._prevLabelBySelect.get(select) ?? '';
    const to = optionLabel(select.options[select.selectedIndex]);
    this._trackSelectPrevious(select);
    if (!to || from === to) return;

    const extras =
      typeof readPdpEventExtras === 'function' ? readPdpEventExtras() : {};

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'custom_event',
      event_name: COLOR_CHANGE_EVENT,
      parameter: JSON.stringify({
        type: 'product',
        from,
        to,
        product_name: this._giftProductName(dropdown),
        category: extras.product_collection || ''
      })
    });
  }
}

if (!customElements.get('amplify-buy-x-get-y-widget')) {
  customElements.define('amplify-buy-x-get-y-widget', AmplifyBuyXGetYWidget);
}
